import useClientPagination from 'hooks/use-client-pagination'
import UrlPagination from 'components/molecules/url-pagination'
import { useHistory } from 'react-router'
import InventoriesApiV2 from 'api/v2/admins/inventories-api-v2'
import ProductLocationsApiV2 from 'api/v2/admins/product-locations-api-v2'
import RedirectButton from 'components/atoms/redirect-button'
import TitlePage from 'components/atoms/title-page'
import DataTable from 'components/organisms/data-table'
import { ALERT_TYPES } from 'constants/alert-constants'
import { LOCATION_TYPE_OPTIONS, getLocationTypeLabel } from 'domains/bnk-warehouse-location/constants/location-type-labels'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useState } from 'react'
import { Badge, Button, ButtonGroup, Card, CardBody, Col, Input, Row, Table } from 'reactstrap'

const propTypes = {
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const locationListCellPropTypes = {
  cell: PropTypes.shape({
    value: PropTypes.array
  })
}

const inventoryActionCellPropTypes = {
  cell: PropTypes.shape({
    row: PropTypes.shape({
      original: PropTypes.shape({
        locations: PropTypes.array
      })
    })
  })
}

const VIEW_OPTIONS = {
  INVENTORY_LOCATIONS : 'inventory_locations',
  LOCATION_SETUP      : 'location_setup'
}

const resolveInitialLocationType = () => {
  const typeName = new URLSearchParams(window.location.search).get('type_name') || ''

  return LOCATION_TYPE_OPTIONS.some((option) => option.key === typeName) ? typeName : ''
}

const locationMasterListPath = ({ query, activeType }) => {
  const params = new URLSearchParams(window.location.search)

  if (query.trim()) params.set('q', query.trim())
  else params.delete('q')
  if (activeType) params.set('type_name', activeType)
  else params.delete('type_name')

  const search = params.toString()
  return `/app/warehouse-team/location/list-page${search ? `?${search}` : ''}`
}

const LocationListCell = ({ cell: { value } }) => {
  const locations = Array.isArray(value) ? value : []

  if (locations.length === 0) return '-'

  return locations.map((location, index) => (
    <div key={ location.id || location.location_id || index }>
      { location.sublocation || location.name || '-' }
    </div>
  ))
}

LocationListCell.propTypes = locationListCellPropTypes

const InventoryActionCell = ({ cell: { row } }) => {
  const locations = Array.isArray(row.original.locations) ? row.original.locations : []
  const editableLocation = locations.find((location) => location.location_id)

  if (!editableLocation) return '-'

  return (
    <RedirectButton
      color='info'
      size='sm'
      text='Edit'
      path={ `/app/warehouse-team/location/${editableLocation.location_id}` }
    />
  )
}

InventoryActionCell.propTypes = inventoryActionCellPropTypes

const BnkWarehouseLocationListPage = ({ pageUtils }) => {
  const history = useHistory()
  const [ activeView, setActiveView ]             = useState(VIEW_OPTIONS.LOCATION_SETUP)
  const [ inventories, setInventories ]           = useState([])
  const [ pagination, setPagination ]             = useState({})
  const [ isLoading, setIsLoading ]               = useState(false)
  const [ productLocations, setProductLocations ] = useState([])
  const [ query, setQuery ]                       = useState(() => new URLSearchParams(window.location.search).get('q') || '')
  const [ activeType, setActiveType ]             = useState(resolveInitialLocationType)

  const fetchLocations = (params = {}) => {
    ProductLocationsApiV2.get(params)
      .then((response) => {
        setProductLocations(response.data.product_locations || [])
      })
      .catch((error) => {
        pageUtils.setApiErrorMsg?.(error.response?.data || error.message)
        pageUtils.setAlertMsg?.('Unable to fetch product locations', ALERT_TYPES.DANGER)
      })
  }

  useEffect(() => {
    if (activeView !== VIEW_OPTIONS.LOCATION_SETUP) return

    const nextUrl = locationMasterListPath({ query, activeType })
    if (`${history.location.pathname}${history.location.search}` !== nextUrl) history.replace(nextUrl)
    fetchLocations({ q: query, type_name: activeType })
  }, [ query, activeType, activeView ]) // eslint-disable-line react-hooks/exhaustive-deps

  const counts = useMemo(() => {
    return productLocations.reduce((memo, location) => {
      memo[location.type_name] = (memo[location.type_name] || 0) + 1
      return memo
    }, {})
  }, [ productLocations ])

  const inventoryLocationColumns = useMemo(() => [
    {
      Header   : 'SKU',
      id       : 'product_sku',
      accessor : (row) => row.product_sku || row.inventory_sku || '-'
    },
    {
      Header   : 'Product Name',
      id       : 'q',
      accessor : (row) => row.product_name || '-'
    },
    {
      Header   : 'Sub Location Name',
      id       : 'sublocation',
      accessor : 'locations',
      Cell     : LocationListCell
    }
  ], [])

  const handleFetchInventories = (tableState) => {
    setIsLoading(true)
    InventoriesApiV2.get({ tableState })
      .then((response) => {
        setInventories(response.data.inventories || [])
        setPagination(response.data.meta || {})
      })
      .catch((error) => pageUtils.setApiErrorMsg?.(error.response?.data || error.message))
      .finally(() => setIsLoading(false))
  }

  const renderInventoryLocationsTable = () => (
    <DataTable
      data={ inventories }
      columns={ inventoryLocationColumns }
      pagination={ pagination }
      isLoading={ isLoading }
      onFetchData={ handleFetchInventories }
    />
  )

  const setupPagination = useClientPagination(productLocations, { enabled: activeView === VIEW_OPTIONS.LOCATION_SETUP })

  const renderLocationSetupTable = () => (
    <>
      <Row className='mb-3'>
        <Col md={ 4 }>
          <Input
            value={ query }
            onChange={ (event) => { setupPagination.setPage(1); setQuery(event.target.value) } }
            placeholder='Search location name / type'
          />
        </Col>
      </Row>

      <Table hover responsive>
        <thead>
          <tr>
            <th>{ 'ID' }</th>
            <th>{ 'Name' }</th>
            <th>{ 'Type name' }</th>
            <th>{ 'Active' }</th>
            <th>{ 'Action' }</th>
          </tr>
        </thead>
        <tbody>
          {
            setupPagination.rows.map((location) => (
              <tr key={ location.id }>
                <td>{ location.id }</td>
                <td>{ location.name }</td>
                <td>{ getLocationTypeLabel(location.type_name) }</td>
                <td>
                  {
                    location.active
                      ? <Badge color='success'>{ 'Active' }</Badge>
                      : <Badge color='secondary'>{ 'Inactive' }</Badge>
                  }
                </td>
                <td>
                  <RedirectButton
                    color='info'
                    size='sm'
                    text='Edit'
                    path={ `/app/warehouse-team/location/${location.id}?return_to=${encodeURIComponent(locationMasterListPath({ query, activeType }))}` }
                  />
                </td>
              </tr>
            ))
          }
        </tbody>
      </Table>
      <UrlPagination pagination={ setupPagination } />
    </>
  )

  return (
    <>
      <TitlePage mainTitle={ 'Location Master' } />

      <Row className='mb-3'>
        <Col md={ 8 }>
                    { /*
+            Temporarily hide Inventory Locations tab.
+            Keep this block for future use when inventory-location view is needed again.
+
+            <ButtonGroup>
+              <Button
+                color={ activeView === VIEW_OPTIONS.INVENTORY_LOCATIONS ? 'primary' : 'secondary' }
+                outline={ activeView !== VIEW_OPTIONS.INVENTORY_LOCATIONS }
+                onClick={ () => setActiveView(VIEW_OPTIONS.INVENTORY_LOCATIONS) }
+              >
+                { 'Inventory Locations' }
+              </Button>
+
+              <Button
+                color={ activeView === VIEW_OPTIONS.LOCATION_SETUP ? 'primary' : 'secondary' }
+                outline={ activeView !== VIEW_OPTIONS.LOCATION_SETUP }
+                onClick={ () => setActiveView(VIEW_OPTIONS.LOCATION_SETUP) }
+              >
+                { 'Location Setup' }
+              </Button>
+            </ButtonGroup>
+          */ }
        </Col>

        <Col md={ 4 } className='text-right'>
          <RedirectButton
            color='primary'
            text='Create Location'
            path='/app/warehouse-team/location/create-page'
          />
        </Col>
      </Row>

      {
        activeView === VIEW_OPTIONS.LOCATION_SETUP && (
          <Row className='mb-3'>
            <Col>
              <ButtonGroup>
                {
                  LOCATION_TYPE_OPTIONS.map((option) => (
                    <Button
                      key={ option.key || 'all' }
                      color={ activeType === option.key ? 'primary' : 'secondary' }
                      outline={ activeType !== option.key }
                      onClick={ () => { setupPagination.setPage(1); setActiveType(option.key) } }
                    >
                      { `${option.label} (${option.key ? (counts[option.key] || 0) : productLocations.length})` }
                    </Button>
                  ))
                }
              </ButtonGroup>
            </Col>
          </Row>
        )
      }

      <Card>
        <CardBody>
          {
            activeView === VIEW_OPTIONS.INVENTORY_LOCATIONS
              ? renderInventoryLocationsTable()
              : renderLocationSetupTable()
          }
        </CardBody>
      </Card>
    </>
  )
}

BnkWarehouseLocationListPage.propTypes = propTypes

export default BnkWarehouseLocationListPage